
<script setup>

  import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';

  import { useMediaControls } from '@vueuse/core';

  import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';

  import { PlayIcon, PauseIcon, SpeakerWaveIcon, SpeakerXMarkIcon, Cog6ToothIcon, CheckCircleIcon } from '@heroicons/vue/24/solid';

  import { LanguageIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/vue/24/outline';

  import { formatTime } from '../utils.js';

  import AudioDescriptionsIcon from '../icons/audio-descriptions-icon.vue';

  import SpinnerIcon from '../icons/spinner-icon.vue';

  import Scrubber from './scrubber.vue';

  const props = defineProps( {

    src: String,

    poster: String,

    tracks: Array,

    loop: { type: Boolean, default: false },

    autoplay: { type: Boolean, default: false }

  } );

  const emit = defineEmits( [ 'ended' ] );

  const video = ref( null );

  const uttering = ref( false );

  const intent = ref( false );

  const { waiting, buffered, playing, currentTime, duration, muted, volume, selectedTrack, tracks, enableTrack, disableTrack } =
  
    useMediaControls( video, { src: props.src, tracks: props.tracks } );

  const subtitles = computed( () => tracks.value.filter( track => track.kind === 'subtitles' ) );

  const captions = computed( () => tracks.value.filter( track => track.kind === 'captions' ) );

  const captionsShowing = ref( false );

  const descriptions = computed( () => tracks.value.filter( track => track.kind === 'descriptions' ) );

  const descriptionsShowing = ref( false );

  const endBuffer = computed( () => buffered.value.length > 0 ? buffered.value[ buffered.value.length - 1 ][ 1 ] : 0 );

  const utterance = new SpeechSynthesisUtterance();

  utterance.onstart = event => uttering.value = true;

  utterance.onend = event => uttering.value = false;

  onMounted( () => {

    video.value.textTracks.addEventListener( 'change', event => {
      
      speechSynthesis.cancel();

      uttering.value = false;

      const track = Array.from( video.value.textTracks ).find( track => track.mode === 'showing' );

      captionsShowing.value = track && track.kind === 'captions';

      descriptionsShowing.value = track && track.kind === 'descriptions';

      if ( track && track.kind === 'descriptions' ) {
        
        track.oncuechange = event => {

          Array.from( track.activeCues ).forEach( cue => {
            
            speechSynthesis.cancel();

            utterance.text = cue.text.replace( /\n/g, ' ' );

            speechSynthesis.speak( utterance );
          
          } );

        }

      }

    } );

    if ( props.autoplay ) intent.value = true;

  } );

  onBeforeUnmount( () => speechSynthesis.cancel() );

  watch( [ volume, muted ], ( [ volume, muted ] ) => utterance.volume = muted ? 0 : volume, { immediate: true } );

  watch( intent, intent => {

    if ( intent && speechSynthesis.paused ) speechSynthesis.resume();

    else if ( ! intent && speechSynthesis.speaking ) speechSynthesis.pause();
    
    else playing.value = intent;
  
  } );

  watch( uttering, uttering => playing.value = uttering ? false : intent.value );

</script>

<template>

  <div class="grid outline-none text-white bg-black" :tabindex="0" autofocus @keydown.prevent.space="intent = ! intent">

    <video ref="video" crossorigin="anonymous"
    
      class="row-start-1 col-start-1 aspect-video w-full object-cover object-center"

      :poster="props.poster" :loop="props.loop" playsinline @click="intent = ! intent" @ended="emit( 'ended' )" />

    <div v-if="descriptions.length" class="row-start-1 col-start-1 flex items-start justify-end">

      <button class="relative flex gap-2 items-center p-4 pointer-events-auto"
      
        @click="descriptionsShowing ? disableTrack() : enableTrack( descriptions[ 0 ] )"
        
        role="switch" aria-label="toggle audio descriptions" :aria-checked="descriptionsShowing">

        <AudioDescriptionsIcon class="w-9" />
                
        <div class="w-6 h-4 font-bold text-xs text-black bg-white rounded-sm">
          
          {{ descriptionsShowing ? 'on' : 'off' }}
        
        </div>

      </button>

    </div>

    <div v-if="waiting" class="row-start-1 col-start-1 flex items-center justify-center pointer-events-none">
    
      <SpinnerIcon class="w-32" />
    
    </div>

    <div class="flex items-center h-12 row-start-2 col-start-1">

      <button class="flex items-center justify-center w-12 h-full" @click="intent = ! intent" type="button" aria-label="play or pause video">
      
        <PlayIcon class="w-5" v-if="! intent" />

        <PauseIcon class="w-5" v-if="intent" />
      
      </button>

      <div class="hidden md:block whitespace-nowrap mx-4">

        {{ formatTime( currentTime ) }} / {{ formatTime( duration ) }}

      </div>

      <Scrubber class="grow-[4] mx-4" v-model="currentTime" :max="duration" :secondary="endBuffer" aria-label="video time scrubber">

        <template #default="{ position, pendingValue }">
          
          <div class="absolute transform -translate-x-1/2 bg-black rounded px-2 bottom-0 mb-4 py-1 text-xs text-white" :style="{ left: position }">

            {{ formatTime( pendingValue ) }}

          </div>

        </template>

      </Scrubber>

      <button class="flex items-center justify-center w-12 h-full" @click="muted = ! muted" aria-label="mute or unmute video">
      
        <SpeakerWaveIcon class="w-5" v-if="! muted" />

        <SpeakerXMarkIcon class="w-5" v-if="muted" />
      
      </button>

      <Scrubber class="hidden md:block w-32 mx-4" v-model="volume" :max="1" aria-label="video volume scrubber" />

      <Menu as="div" class="relative h-full">

        <MenuButton class="flex items-center justify-center w-12 h-full"
        
          aria-label="open or close the captions and descriptions menu">

          <Cog6ToothIcon class="w-5" />

        </MenuButton>

        <MenuItems class="overflow-hidden absolute -top-2 right-2 z-10 -translate-y-full text-sm bg-black">
        
          <div class="flex gap-2 px-4 py-2 text-black bg-white" v-if="subtitles.length">
            
            <span class="grow">Subtitles</span>
            
            <LanguageIcon class="w-5" />
          
          </div>

          <MenuItem as="button" v-slot="{ active }" class="flex items-center justify-between w-full gap-4 h-12 px-4 py-2"
          
            v-for="track in subtitles" :key="track.id" @click="enableTrack( track )"

            :aria-label="`turn on the ${ track.label } subtitle track`">

            <span aria-hidden="true" :class="[ 'whitespace-nowrap', { 'underline': active } ]">{{ track.label }}</span>

            <CheckCircleIcon class="w-5" v-if="track.mode === 'showing'" />

          </MenuItem>

          <div class="flex gap-2 px-4 py-2 text-black bg-white" v-if="captions.length">
            
            <span class="grow">Captions</span>
            
            <ChatBubbleBottomCenterTextIcon class="w-5" />
          
          </div>

          <MenuItem as="button" v-slot="{ active }" class="flex items-center justify-between w-full gap-4 h-12 px-4 py-2"
          
            v-for="track in captions" :key="track.id" @click="enableTrack( track )"
            
            :aria-label="`turn on the ${ track.label } description track`">

            <span aria-hidden="true" :class="[ 'whitespace-nowrap', { 'underline': active } ]">{{ track.label }}</span>

            <CheckCircleIcon class="w-5" v-if="track.mode === 'showing'" />

          </MenuItem>

          <div class="flex gap-2 px-4 py-2 text-black bg-white" v-if="descriptions.length">
            
            <span class="grow">Descriptions</span>

            <AudioDescriptionsIcon class="w-7" />
          
          </div>

          <MenuItem as="button" v-slot="{ active }" class="flex items-center justify-between w-full gap-4 h-12 px-4 py-2"
          
            v-for="track in descriptions" :key="track.id" @click="enableTrack( track )"
            
            :aria-label="`turn on the ${ track.label } description track`">

            <span aria-hidden="true" :class="[ 'whitespace-nowrap', { 'underline': active } ]">{{ track.label }}</span>

            <CheckCircleIcon class="w-5" v-if="track.mode === 'showing'" />

          </MenuItem>
        
          <MenuItem as="button" class="flex items-center justify-between w-full gap-4 h-12 px-4 py-2" @click="disableTrack()"
          
            aria-label="turn off all tracks">

            <span aria-hidden="true">Off</span>

            <CheckCircleIcon class="w-5" v-if="selectedTrack === -1" />

          </MenuItem>

        </MenuItems>

      </Menu>

    </div>

  </div>

</template>
